/**
 * Blossom — product-form.js
 * <product-form> — progressively enhances a Shopify product form
 * ({% form 'product' %}) with AJAX add-to-cart, loading/success/error states
 * and cart drawer opening. Without JavaScript the form posts normally.
 *
 * Contract (markup inside <product-form>):
 *   form[action*="/cart/add"]       the Shopify product form
 *   input[name="id"]                 selected variant id
 *   button[type="submit"][name=add]  add to cart button
 *   [data-add-label]                 (optional) label element inside the button
 *   [data-form-error]                error message container (role="alert")
 */
(() => {
  if (!window.Theme || customElements.get('product-form')) return;

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.bound) return;
      this.bound = true;
      this.submitButton = this.form.querySelector('[type="submit"][name="add"]');
      this.errorContainer = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    get variantInput() {
      return this.form.querySelector('input[name="id"]');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      if (this.variantInput && !this.variantInput.value) {
        this.showError(Theme.strings.unavailable);
        return;
      }

      this.hideError();
      this.setLoading(true);

      try {
        await Theme.cart.add(new FormData(this.form), { source: this });
        this.showSuccess();

        // Close a surrounding quick add modal before revealing the cart.
        const modal = this.closest('modal-component');
        if (modal && typeof modal.close === 'function') await modal.close();

        if (Theme.settings.cartType === 'drawer' && document.getElementById('CartDrawer')) {
          Theme.cart.open();
        } else if (!document.querySelector('main-cart')) {
          // On the cart page the cart section has already re-rendered in place.
          window.location.href = Theme.routes.cart;
        }
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      this.submitButton.setAttribute('aria-busy', String(loading));
      if (loading) this.submitButton.setAttribute('disabled', '');
      else if (this.submitButton.dataset.soldOut !== 'true') this.submitButton.removeAttribute('disabled');
    }

    showSuccess() {
      const label = this.submitButton?.querySelector('[data-add-label]');
      if (!label) return;
      const original = label.innerHTML;
      label.textContent = Theme.strings.added || 'Added';
      window.setTimeout(() => {
        label.innerHTML = original;
      }, 2000);
    }

    showError(message) {
      if (!this.errorContainer) {
        Theme.toast(message, { type: 'error' });
        return;
      }
      this.errorContainer.textContent = message || Theme.strings.genericError;
      this.errorContainer.hidden = false;
    }

    hideError() {
      if (!this.errorContainer) return;
      this.errorContainer.textContent = '';
      this.errorContainer.hidden = true;
    }
  }

  customElements.define('product-form', ProductForm);
})();
